import type { BlastRadiusScoreDocument } from '../../api-utils/blast';
import type { EntityRiskScoreDocument } from '../../api-utils/entity_risk';
import { toScoreNumber } from './eriUtils';

const ERI_WEIGHTS = {
  success: 0.3,
  volume: 0.25,
  campaigns: 0.35,
  actor: 0.1,
  stake_base: 0.4,
  stake_weight: 0.6,
} as const;

const EBR_CAPS = {
  self: 25,
  control: 55,
  threat: 10,
  exposure: 5,
  business: 5,
} as const;

export interface WeightedBar {
  code: string;
  weight: number;
  component: number;
  product: number;
}

export function lookupByNodeId<T>(map: Map<string, T>, nodeId: string): T | undefined {
  const direct = map.get(nodeId);
  if (direct) return direct;
  const slash = nodeId.lastIndexOf('/');
  if (slash >= 0) {
    const tail = nodeId.slice(slash + 1);
    if (tail) return map.get(tail);
  }
  return undefined;
}

export function hasEriData(row: EntityRiskScoreDocument | undefined | null): row is EntityRiskScoreDocument {
  if (!row) return false;
  const score = toScoreNumber(row.score);
  if (score != null && score > 0) return true;
  const nActions = Math.max(
    Array.isArray(row.attacking_actions) ? row.attacking_actions.length : 0,
    toScoreNumber(row.breakdown?.n_actions) ?? 0
  );
  const nCampaigns = Math.max(
    Array.isArray(row.attacking_campaigns) ? row.attacking_campaigns.length : 0,
    toScoreNumber(row.breakdown?.n_campaigns) ?? 0
  );
  return nActions > 0 || nCampaigns > 0;
}

export function ebrScoreOf(row: BlastRadiusScoreDocument | undefined | null): number | null {
  if (!row) return null;
  return toScoreNumber(row.ebr_score) ?? toScoreNumber(row.score);
}

export function titleCase(value: string): string {
  const cleaned = String(value || '').replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function num(record: Record<string, number | string> | undefined, key: string, fallback: number): number {
  const value = toScoreNumber(record?.[key]);
  return value == null ? fallback : value;
}

export function eriBars(row: EntityRiskScoreDocument): WeightedBar[] {
  const breakdown = row.breakdown || {};
  const weights = row.weights || {};
  return (
    [
      ['success', 'SUC'],
      ['volume', 'VOL'],
      ['campaigns', 'CMP'],
      ['actor', 'ACT'],
    ] as const
  ).map(([key, code]) => {
    const weight = num(weights, key, ERI_WEIGHTS[key]);
    const part = num(breakdown, key, 0);
    const component = weight > 0 ? (part / weight) * 100 : 0;
    return { code, weight, component, product: weight * component };
  });
}

export function eriFormula(row: EntityRiskScoreDocument): {
  intensity: number;
  amp: number;
  raw: number;
  score: number;
} {
  const breakdown = row.breakdown || {};
  const weights = row.weights || {};
  const intensity = num(breakdown, 'intensity', 0);
  const atStake = num(breakdown, 'at_stake', 0);
  const base = num(weights, 'stake_base', ERI_WEIGHTS.stake_base);
  const stakeW = num(weights, 'stake_weight', ERI_WEIGHTS.stake_weight);
  const amp = base + stakeW * atStake;
  const raw = 100 * intensity * amp;
  return {
    intensity,
    amp,
    raw,
    score: toScoreNumber(row.score) ?? Math.round(Math.max(0, Math.min(100, raw))),
  };
}

export function ebrBars(row: BlastRadiusScoreDocument): WeightedBar[] {
  const breakdown = row.breakdown || {};
  return (
    [
      ['self', 'SLF'],
      ['control', 'CTL'],
      ['threat', 'THR'],
      ['exposure', 'EXP'],
      ['business', 'BIZ'],
    ] as const
  ).map(([key, code]) => {
    const cap = EBR_CAPS[key];
    const product = num(breakdown, key, 0);
    return { code, weight: cap / 100, component: product, product };
  });
}

export function driverSignals(row: EntityRiskScoreDocument): { label: string; signals: number } {
  const drivers = Array.isArray(row.top_drivers) ? row.top_drivers : [];
  const first = drivers.find((item) => String(item.label || '').trim());
  const nActions = Math.max(
    Array.isArray(row.attacking_actions) ? row.attacking_actions.length : 0,
    toScoreNumber(row.breakdown?.n_actions) ?? 0
  );
  return {
    label: String(first?.label || '—'),
    signals: drivers.length || nActions,
  };
}
